import { Box, makeStyles, Typography } from "@material-ui/core";
import PropTypes from "prop-types";
import SquarkNewLogo from '~/src/assets/squark-new-logo-community-white.svg'
import React, { ReactNode } from "react";
import { Link } from "react-router-dom";

interface ButtonItem {
  link: string;
  label: string;
}

interface NewLayoutProps {
  cursorBeforeText?: string;
  cursorAfterText?: string;
  description?: string;
  chipText?: string;
  buttonArray?: ButtonItem[];
  children: ReactNode;
}

const useStyles = makeStyles(({ breakpoints, spacing }) => ({
  "@keyframes blink": {
    "0%, 49%": { opacity: 1 },
    "50%, 100%": { opacity: 0 },
  },
  buttonLinkStyle: {
    borderWidth: "2px",
    borderRadius: "25px",
    textDecoration: "none",
    lineHeight: "1.6",
    fontWeight: 600,
    background: "#000000",
    color: "#FFFFFF",
    backgroundColor: "#0F0F0F",
    padding: "16px 32px",
    transition: "background-color 200ms ease-in-out, color 200ms ease-in-out",
    "&:hover": {
      backgroundColor: "#1C1C1C",
      color: "#FFFFFF",
    },
  },
  cursor: {
    display: "inline-block",
    width: "2px",
    height: "0.9em",
    background: "#00aaff",
    marginRight: "10px",
    verticalAlign: "-0.06em",
    animation: "$blink 1s steps(2, start) infinite",
  },
  layoutContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#000000",
    "& *": {
      fontFamily: '"Inter", sans-serif',
    },
  },
  innerContainer: {
    gap: "clamp(16px, 3vw, 40px)",
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "1.15fr 1fr",
    padding: "0 24px",
    [breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  leftContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    "& h1": {
      margin: 0,
      marginBottom: "24px",
      "& span": {
        fontSize: "64px",
        lineHeight: "1.1",
        letterSpacing: "-.02em",
        fontWeight: 500,
      },
    },
  },
  descriptionStyle: {
    margin: 0,
    fontSize: "20px",
    lineHeight: "1.5",
    fontWeight: 400,
    color: "#FFFFFF",
    marginBottom: "32px",
  },
  buttonContainer: {
    gap: "16px",
    display: "flex",
  },
  rightContainer: {
    flex: 1,
    height: "max-content",
    background: "#FFFFFF",
    color: "#000000",
    borderRadius: "16px",
    boxShadow: "0 12px 40px rgba(0, 0, 0, .28)",
    padding: "28px",
    overflow: "hidden",
  },

  chipContainer: {
    width: "max-content",
    display: "inline-block",
    background: "rgba(255, 255, 255, .08)",
    color: "#fff",
    border: "1px solid rgba(255, 255, 255, .18)",
    borderRadius: "999px",
    padding: ".25rem .6rem",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "1.6",
  },
}));

const NewLayout: React.FC<NewLayoutProps> = ({
  cursorBeforeText = "",
  description = "",
  cursorAfterText = "",
  chipText = "",
  buttonArray = [],
  children,
}) => {
  const {
    cursor,
    buttonLinkStyle,
    buttonContainer,
    descriptionStyle,
    rightContainer,
    innerContainer,
    leftContainer,
    chipContainer,
    layoutContainer,
  } = useStyles();

  return (
    <Box display="flex" flex="auto" className={layoutContainer}>
      <Box className={innerContainer}>
        <Box className={leftContainer}>
          <img
            alt="squark logo"
            src={SquarkNewLogo}
            style={{ height: "auto", maxWidth: "150px", marginBottom: "16px" }}
          />
          {chipText && (
            <Box className={chipContainer} marginBottom={"10px"}>
              {chipText}
            </Box>
          )}
          <h1>
            <Typography component={"span"} style={{ color: "#FFFFFF" }}>
              {cursorBeforeText}
            </Typography>
            <Typography component={"span"} className={cursor} />
            <Typography component={"span"} style={{ color: "#aba69c" }}>
              {cursorAfterText}
            </Typography>
          </h1>
          {description && (
            <Typography className={descriptionStyle}>{description}</Typography>
          )}
          <Box className={buttonContainer}>
            {buttonArray.map(({ link, label }) => (
              <Link
                key={`${link}|${label}`}
                className={buttonLinkStyle}
                to={link}
              >
                {label}
              </Link>
            ))}
          </Box>
        </Box>
        <Box className={rightContainer}>{children}</Box>
      </Box>
    </Box>
  );
};

NewLayout.propTypes = {
  children: PropTypes.node,
};

NewLayout.defaultProps = {
  children: null,
};

export default NewLayout;
